import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useCurrentOrganization } from './use-current-organization';
import { useTranslation } from 'react-i18next';

export interface ProjectPriority {
  id: string;
  name: string;
  color?: string;
  isDefault?: boolean;
  organizationId: string;
  createdAt: string;
}

/**
 * Hook to fetch project priorities for the current organization
 */
export function useProjectPriorities() {
  const { organizationId } = useCurrentOrganization();
  
  return useQuery<ProjectPriority[]>({
    queryKey: ["/api/project-priorities", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/project-priorities?organizationId=${encodeURIComponent(organizationId)}` : "/api/project-priorities";
      const response = await apiRequest(url);
      const result = await response.json();
      return result?.data || [];
    },
    // Keep enabled even before organizationId is resolved.
    // Backend can resolve org from authenticated user/session and this avoids
    // race conditions where modal opens before org context is hydrated.
    enabled: true,
    staleTime: 30 * 60 * 1000, // 30 minutes - project priorities rarely change
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

/**
 * Hook to get project priority display information (name, color, localized name)
 */
export function useProjectPriorityDisplay(priorityId: string | null | undefined) {
  const { data: priorities = [] } = useProjectPriorities();
  const { t } = useTranslation(['pages.projects']);
  
  const priority = priorityId ? priorities.find(p => p.id === priorityId) : null;
  
  const getDisplayName = (): string => {
    if (!priority) return '-';
    
    // Use localization for default priorities
    if (priority.isDefault) {
      const priorityMap: Record<string, string> = {
        'Low': t('pages.projects:priority.low'),
        'Middle': t('pages.projects:priority.middle'),
        'High': t('pages.projects:priority.high'),
        'Critical': t('pages.projects:priority.critical'),
      };
      if (priorityMap[priority.name]) {
        return priorityMap[priority.name];
      }
    }
    
    return priority.name;
  };
  
  const getColor = (): string => {
    return priority?.color || '#6b7280';
  };
  
  return {
    priority,
    displayName: getDisplayName(),
    color: getColor(),
    isLoading: !priorities.length && !!priorityId,
  };
}
